import { FC } from "react";
import { PersonInformation } from "@/types";
import { RelationshipToPrimaryOwner } from "@/types/enums";
import { usePersonalAccountBeneficiary } from "@/hooks/usePersonalAccountBeneficiary";
import { getFullName } from "../ComplexFields/PersonInformationComplexField";

import "./PersonalAccountBeneficiaries.scss";

export interface PersonalAccountBeneficiaryTableRowProps {
  financialApplicationId: string;
  personalAccountBeneficiaryId: string;
  onDelete: (personalAccountBeneficiaryId: string) => void;
  validationErrors: Record<string, string[]>;
  readOnly?: boolean;
}

export const PersonalAccountBeneficiaryTableRow: FC<PersonalAccountBeneficiaryTableRowProps> = (props) => {
  const { formValue } = usePersonalAccountBeneficiary(props.financialApplicationId, props.personalAccountBeneficiaryId);

  if (!formValue) {
    return (
      <tr>
        <td colSpan={4}>Loading...</td>
      </tr>
    );
  }

  const personInfo = formValue.personInformation as PersonInformation;
  const fullName = getFullName(personInfo) || "New Beneficiary";

  // Get relationship text
  const getRelationshipText = (relationship?: RelationshipToPrimaryOwner): string => {
    if (!relationship) return "";

    const relationshipMap: Record<RelationshipToPrimaryOwner, string> = {
      [RelationshipToPrimaryOwner.Spouse]: "Spouse",
      [RelationshipToPrimaryOwner.DomesticPartner]: "Domestic Partner",
      [RelationshipToPrimaryOwner.Parent]: "Parent",
      [RelationshipToPrimaryOwner.LegalGuardian]: "Legal Guardian",
      [RelationshipToPrimaryOwner.Child]: "Child",
      [RelationshipToPrimaryOwner.Sibling]: "Sibling",
      [RelationshipToPrimaryOwner.OtherRelative]: "Other Relative",
      [RelationshipToPrimaryOwner.BusinessPartnerOrCoOwner]: "Business Partner/Co-Owner",
      [RelationshipToPrimaryOwner.AccountantOrBookkeeper]: "Accountant/Bookkeeper",
      [RelationshipToPrimaryOwner.Employee]: "Employee",
      [RelationshipToPrimaryOwner.TrusteeOrPowerOfAttorney]: "Trustee/Power of Attorney",
      [RelationshipToPrimaryOwner.Friend]: "Friend",
      [RelationshipToPrimaryOwner.Other]: "Other",
    };

    return relationshipMap[relationship] || "";
  };

  // Get bequests text
  const getBequestsText = (): string => {
    if (!formValue.personalAccountBequests || formValue.personalAccountBequests.length === 0) {
      return "No beneficiary designations";
    }
    
    return formValue.personalAccountBequests
      .map((bequest: any) => `${bequest.financialProductName} (${bequest.percentageBequeathed.toFixed(2)}%)`)
      .join(", ");
  };

  return (
    <tr className="personal-account-beneficiaries-table__row">
      <td className="personal-account-beneficiaries-table__cell personal-account-beneficiaries-table__cell--relationship">
        {getRelationshipText(formValue.relationshipToPrimaryOwner as RelationshipToPrimaryOwner | undefined)}
      </td>
      <td className="personal-account-beneficiaries-table__cell personal-account-beneficiaries-table__cell--name">
        {fullName}
      </td>
      <td className="personal-account-beneficiaries-table__cell personal-account-beneficiaries-table__cell--bequests">
        {getBequestsText()}
      </td>
    </tr>
  );
}; 